/** Merch widgets with this many products render as a store grid; fewer render as a list. */
export const CATALOG_GRID_MIN_PRODUCTS = 6;

/** Fallback when a product has no `lowStockThreshold`. */
export const DEFAULT_LOW_STOCK_THRESHOLD = 10;

/** Below this many units the customer sees an exact "Only N left" count. */
export const LAST_FEW_THRESHOLD = 5;

/**
 * Whether an "Only N left" nudge is worth showing. Skipped when the total
 * capacity is itself below the threshold (e.g. a 1-seat session), since it
 * would always read "Only 1 left" and say nothing about demand.
 */
export const shouldShowLastFew = (
  remaining: number | null | undefined,
  totalCapacity: number | null | undefined,
): boolean =>
  remaining != null &&
  remaining > 0 &&
  remaining < LAST_FEW_THRESHOLD &&
  (totalCapacity ?? 0) >= LAST_FEW_THRESHOLD;

export type StockSignal = "sold_out" | "last_few" | "going_fast" | "ok";

export const getStockSignal = (
  remaining: number,
  lowStockThreshold: number | null | undefined = DEFAULT_LOW_STOCK_THRESHOLD,
): StockSignal => {
  if (remaining <= 0) return "sold_out";
  if (remaining < LAST_FEW_THRESHOLD) return "last_few";
  if (remaining <= (lowStockThreshold ?? DEFAULT_LOW_STOCK_THRESHOLD)) {
    return "going_fast";
  }
  return "ok";
};

/**
 * A product with no options sells through one default variant that carries its
 * price, SKU and stock. Adding options later turns it into the first real variant.
 */
export const isDefaultMerchVariant = (
  variant:
    | { dimensionValueIds?: ReadonlyArray<string | null> | null }
    | null
    | undefined,
) => !!variant && !variant.dimensionValueIds?.length;

type ImageLike = { _id?: string | null };

type MerchImageSource<TImage extends ImageLike> = {
  images?: ReadonlyArray<TImage | null> | null;
  merchMetadata?: {
    dimensions?: ReadonlyArray<{
      _id?: string | null;
      drivesImages?: boolean | null;
      values?: ReadonlyArray<{
        _id?: string | null;
        imageIds?: ReadonlyArray<string | null> | null;
      } | null> | null;
    } | null> | null;
    variants?: ReadonlyArray<{
      _id?: string | null;
      dimensionValueIds?: ReadonlyArray<string | null> | null;
    } | null> | null;
  } | null;
};

/** The dimension whose values pick photos (e.g. Colour), if the merchant set one. */
export const getImageDimension = <TImage extends ImageLike>(
  product: MerchImageSource<TImage> | null | undefined,
) => product?.merchMetadata?.dimensions?.find(d => d?.drivesImages) ?? null;

/** Product images tagged with a dimension value, in gallery order. Stale ids are ignored. */
export const getValueImages = <TImage extends ImageLike>(
  product: MerchImageSource<TImage> | null | undefined,
  valueId: string | null | undefined,
): TImage[] => {
  if (!product || !valueId) return [];
  const value = getImageDimension(product)?.values?.find(v => v?._id === valueId);
  const tagged = new Set(value?.imageIds?.filter(Boolean) ?? []);
  if (tagged.size === 0) return [];
  return (product.images ?? []).filter(
    (image): image is TImage => !!image?._id && tagged.has(image._id),
  );
};

/** The best photo for a variant: its image-driving value's first photo, else the product's first. */
export const getVariantImage = <TImage extends ImageLike>(
  product: MerchImageSource<TImage> | null | undefined,
  variantId: string | null | undefined,
): TImage | null => {
  const fallback = product?.images?.find(Boolean) ?? null;
  const imageDim = getImageDimension(product);
  if (!imageDim || !variantId) return fallback;
  const variant = product?.merchMetadata?.variants?.find(v => v?._id === variantId);
  const valueId = variant?.dimensionValueIds?.find(id =>
    imageDim.values?.some(v => v?._id === id),
  );
  return getValueImages(product, valueId)[0] ?? fallback;
};
